import React, { Fragment, useEffect, useState } from "react";
import { Row, Table, Button, Col } from "reactstrap";
import { Link } from "react-router-dom";

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';
import JobListGlobalFilter from "./GlobalSearchFilter";

/**
 * Calcula el rango de páginas visibles para el paginador
 * @param {number} currentPage - Índice de la página actual (0-based)
 * @param {number} totalPages - Total de páginas disponibles
 * @param {number} maxVisiblePages - Máximo de páginas visibles (default: 7)
 * @returns {object} Objeto con startPage, endPage, showStartEllipsis, showEndEllipsis
 */
const calculatePageRange = (currentPage, totalPages, maxVisiblePages = 7) => {
  // Si total de páginas <= maxVisiblePages, mostrar todas
  if (totalPages <= maxVisiblePages) {
    return {
      startPage: 0,
      endPage: totalPages - 1,
      showStartEllipsis: false,
      showEndEllipsis: false
    };
  }

  // Calcular mitad del rango visible para centrar la página actual
  const halfVisible = Math.floor(maxVisiblePages / 2);

  // Calcular inicio y fin del rango
  let startPage = Math.max(0, currentPage - halfVisible);
  let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);

  // Ajustar si estamos cerca del final
  if (endPage - startPage < maxVisiblePages - 1) {
    startPage = Math.max(0, endPage - maxVisiblePages + 1);
  }

  // Determinar si mostrar ellipsis
  const showStartEllipsis = startPage > 0;
  const showEndEllipsis = endPage < totalPages - 1;

  return { startPage, endPage, showStartEllipsis, showEndEllipsis };
};

// Column Filter
const Filter = ({
  column
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? '')}
        onChange={value => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + 'list'}
      />
      <div className="h-1" />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <React.Fragment>
      <Col sm={4}>
        <input {...props} value={value} onChange={e => setValue(e.target.value)} />
      </Col>
    </React.Fragment>
  );
};

const TableContainer = ({
  columns,
  data,
  tableClass,
  theadClass,
  divClassName,
  isBordered,
  isPagination,
  isGlobalFilter,
  paginationWrapper,
  SearchPlaceholder,
  pagination,
  buttonClass,
  buttonName,
  isAddButton,
  isCustomPageSize,
  handleUserClick,
  isJobListGlobalFilter,
  pageIndex: controlledPageIndex,
  setPageIndex: setControlledPageIndex,
  pageSize: controlledPageSize,
  setPageSize: setControlledPageSize,
  totalRecords = 0,
  maxVisiblePages = 7, // Nueva prop para controlar páginas visibles
  onClearFilters = null, // Nueva prop opcional para comunicar cuando se limpian todos los filtros
  onFilterChange = null, // Nueva prop para notificar cambios en filtros
}) => {

  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [previousFilters, setPreviousFilters] = useState({ columnFilters: [], globalFilter: '' });
  const [isFiltering, setIsFiltering] = useState(false);

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank
    });
    return itemRank.passed;
  };

  // Detectar cambios en filtros
  useEffect(() => {
    const currentFilters = { columnFilters, globalFilter };
    const hasFiltersChanged = 
      JSON.stringify(currentFilters.columnFilters) !== JSON.stringify(previousFilters.columnFilters) ||
      currentFilters.globalFilter !== previousFilters.globalFilter;

    if (hasFiltersChanged) {
      if (columnFilters.length > 0 || globalFilter.trim() !== '') {
        // Se aplicaron filtros
        setIsFiltering(true);
        setPreviousFilters(currentFilters);
        
        // Notificar al padre que hay filtros activos
        if (onFilterChange) {
          onFilterChange({ hasFilters: true, columnFilters, globalFilter });
        }
        
        setTimeout(() => {
          setIsFiltering(false);
        }, 200);
      } else {
        // Se limpiaron todos los filtros - comunicar al padre para recargar datos
        if (onClearFilters) {
          onClearFilters();
        }
        
        // Notificar al padre que no hay filtros
        if (onFilterChange) {
          onFilterChange({ hasFilters: false, columnFilters: [], globalFilter: '' });
        }
        
        setPreviousFilters(currentFilters);
      }
    }
  }, [columnFilters, globalFilter, previousFilters, onClearFilters, onFilterChange]);

  // Función para limpiar todos los filtros
  const clearAllFilters = () => {
    setColumnFilters([]);
    setGlobalFilter('');
    setPreviousFilters({ columnFilters: [], globalFilter: '' });
  };

  // Función para limpiar un filtro específico
  const clearColumnFilter = (columnId) => {
    const newColumnFilters = columnFilters.filter(filter => filter.id !== columnId);
    setColumnFilters(newColumnFilters);
  };

  // Contar filtros activos
  const activeFiltersCount = columnFilters.length + (globalFilter.trim() !== '' ? 1 : 0);

  const table = useReactTable({
    columns,
    data,
    // Restaurar paginación manual basada en filtros (comportamiento estable)
    manualPagination: !activeFiltersCount,
    pageCount: activeFiltersCount ? undefined : Math.ceil(totalRecords / (controlledPageSize || 10)),
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
      // Asegurar que pagination nunca sea undefined para evitar errores en getPaginationRowModel
      pagination: {
        pageIndex: controlledPageIndex ?? 0,
        pageSize: controlledPageSize ?? 10,
      },
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(), // Filtrado client-side de los datos actuales
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: (updater) => {
      if (setControlledPageIndex && setControlledPageSize) {
        if (typeof updater === 'function') {
          const next = updater({ pageIndex: controlledPageIndex, pageSize: controlledPageSize });
          setControlledPageIndex(next.pageIndex);
          setControlledPageSize(next.pageSize);
        } else if (typeof updater === 'object') {
          if (updater.pageIndex !== undefined) setControlledPageIndex(updater.pageIndex);
          if (updater.pageSize !== undefined) setControlledPageSize(updater.pageSize);
        }
      }
    },
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    // setPageSize,
    getState
  } = table;

  // Navegar a página 1 cuando se aplican filtros
  useEffect(() => {
    if (columnFilters.length > 0 || globalFilter.trim() !== '') {
      // Navegar a página 1 solo si no estamos ya en la página 1
      if (setControlledPageIndex && controlledPageIndex !== 0) {
        setControlledPageIndex(0);
      }
    }
  }, [columnFilters, globalFilter, setControlledPageIndex, controlledPageIndex]);

  // useEffect(() => {
  //   Number(customPageSize) && setPageSize(Number(customPageSize));
  // }, [customPageSize, setPageSize]);

  return (
    <Fragment>

      <Row className="mb-2">
        {isCustomPageSize && (
          <Col sm={4}>
            <select
              className="form-select pageSize mb-2"
              style={{ minWidth: 180 }}
              value={table.getState().pagination.pageSize}
              onChange={e => {
                table.setPageSize(Number(e.target.value))
              }}
            >
              {[10, 20, 30, 40, 50].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  Mostrar {pageSize}
                </option>
              ))}
            </select>
          </Col>
        )}

        {isGlobalFilter && <DebouncedInput
          value={globalFilter ?? ''}
          onChange={value => setGlobalFilter(String(value))}
          className="form-control search-box me-2 mb-2 d-inline-block"
          placeholder={SearchPlaceholder}
        />}

        {isJobListGlobalFilter && <JobListGlobalFilter setGlobalFilter={setGlobalFilter} />}

        {/* Indicadores de filtros activos */}
        {activeFiltersCount > 0 && (
          <Col sm={12} className="mb-2">
            <div className="d-flex align-items-center flex-wrap gap-2">
              <span className="badge bg-primary">
                {activeFiltersCount} filtro{activeFiltersCount !== 1 ? 's' : ''} activo{activeFiltersCount !== 1 ? 's' : ''}
              </span>
              
              {/* Badges para filtros de columnas */}
              {columnFilters.map((filter, index) => (
                <span key={index} className="badge bg-info d-flex align-items-center">
                  {filter.id}: {filter.value}
                  <button 
                    type="button" 
                    className="btn-close btn-close-white ms-1" 
                    style={{fontSize: '0.7em'}}
                    onClick={() => clearColumnFilter(filter.id)}
                    aria-label="Eliminar filtro"
                  ></button>
                </span>
              ))}
              
              {/* Badge para filtro global */}
              {globalFilter.trim() !== '' && (
                <span className="badge bg-success d-flex align-items-center">
                  Búsqueda: {globalFilter}
                  <button 
                    type="button" 
                    className="btn-close btn-close-white ms-1" 
                    style={{fontSize: '0.7em'}}
                    onClick={() => setGlobalFilter('')}
                    aria-label="Eliminar búsqueda"
                  ></button>
                </span>
              )}
              
              {/* Botón para limpiar todos los filtros */}
              <Button 
                type="button" 
                size="sm" 
                color="outline-secondary"
                onClick={clearAllFilters}
                className="d-flex align-items-center"
              >
                <i className="mdi mdi-filter-remove me-1"></i>
                Limpiar filtros
              </Button>
            </div>
          </Col>
        )}

        {isAddButton && <Col sm={6}>
          <div className="text-sm-end">
            <Button type="button" className={buttonClass} onClick={handleUserClick}>
              <i className="mdi mdi-plus me-1"></i> {buttonName}</Button>
          </div>
        </Col>}
      </Row>

      <div className={divClassName ? divClassName : "table-responsive"}>
        <Table hover className={tableClass} bordered={isBordered}>
          <thead className={theadClass}>
            {getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <th key={header.id} colSpan={header.colSpan} className={`${header.column.columnDef.enableSorting ? "sorting sorting_desc" : ""}`}>
                      {header.isPlaceholder ? null : (
                        <React.Fragment>
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? 'cursor-pointer select-none'
                                : '',
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {
                              {
                                asc: '',
                                desc: '',
                              }
                              [header.column.getIsSorted()] ?? null}
                          </div>
                          {header.column.getCanFilter() ? (
                            <div>
                              <Filter column={header.column} table={table} />
                            </div>
                          ) : null}
                        </React.Fragment>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.map(row => {
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => {
                    return (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>


      {
        isPagination && getState().pagination && (
          <Row className="justify-content-md-end justify-content-center align-items-center">
            <Col>
              <div className="dataTables_info">
                {(() => {
                  const filteredRowsCount = table.getFilteredRowModel().rows.length;
                  const currentPageSize = getState().pagination.pageSize;
                  const currentPageIndex = getState().pagination.pageIndex;
                  const pageCount = table.getPageCount();
                  
                  return activeFiltersCount > 0 ? (
                    <>
                      {isFiltering ? (
                        <span className="text-muted">
                          <i className="mdi mdi-loading mdi-spin me-1"></i>
                          Aplicando filtros...
                        </span>
                      ) : (
                        <>
                          {filteredRowsCount > 0 ? (
                            <>
                              Mostrando {Math.min(currentPageIndex * currentPageSize + 1, filteredRowsCount)} a{" "}
                              {Math.min((currentPageIndex + 1) * currentPageSize, filteredRowsCount)} de{" "}
                              {filteredRowsCount} resultado{filteredRowsCount !== 1 ? 's' : ''} filtrado{filteredRowsCount !== 1 ? 's' : ''}
                              <span className="badge bg-info ms-2">
                                {activeFiltersCount} filtro{activeFiltersCount !== 1 ? 's' : ''} activo{activeFiltersCount !== 1 ? 's' : ''}
                              </span>
                            </>
                          ) : (
                            <>
                              No se encontraron resultados para los filtros aplicados
                              <span className="badge bg-warning ms-2">
                                {activeFiltersCount} filtro{activeFiltersCount !== 1 ? 's' : ''} activo{activeFiltersCount !== 1 ? 's' : ''}
                              </span>
                            </>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      Mostrando {currentPageIndex * currentPageSize + 1} a{" "}
                      {Math.min((currentPageIndex + 1) * currentPageSize, totalRecords)} de{" "}
                      {totalRecords} resultados
                    </>
                  );
                })()}
              </div>
            </Col>
            <Col className="col-md-auto">
              <div className={paginationWrapper}>
                <ul className={pagination}>
                  {/* Botón First */}
                  <li className={`paginate_button page-item ${getState().pagination.pageIndex === 0 ? "disabled" : ""}`}>
                    <Link
                      to="#"
                      className="page-link"
                      onClick={(e) => {
                        e.preventDefault();
                        if (getState().pagination.pageIndex !== 0) {
                          setControlledPageIndex ? setControlledPageIndex(0) : setPageIndex(0);
                        }
                      }}
                      aria-label="Primera página"
                    >
                      <i className="mdi mdi-chevron-double-left"></i>
                    </Link>
                  </li>

                  {/* Botón Previous */}
                  <li className={`paginate_button page-item previous ${!getCanPreviousPage() ? "disabled" : ""}`}>
                    <Link to="#" className="page-link" onClick={previousPage} aria-label="Página anterior">
                      <i className="mdi mdi-chevron-left"></i>
                    </Link>
                  </li>

                  {(() => {
                    const totalPages = getPageOptions().length;
                    const currentPage = getState().pagination.pageIndex;
                    const { startPage, endPage, showStartEllipsis, showEndEllipsis } = calculatePageRange(
                      currentPage,
                      totalPages,
                      maxVisiblePages
                    );

                    return (
                      <>
                        {/* Ellipsis inicial si hay páginas ocultas al inicio */}
                        {showStartEllipsis && (
                          <li className="paginate_button page-item disabled">
                            <span className="page-link" aria-hidden="true">...</span>
                          </li>
                        )}

                        {/* Páginas visibles en el rango calculado */}
                        {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(pageNum => (
                          <li
                            key={pageNum}
                            className={`paginate_button page-item ${currentPage === pageNum ? "active" : ""}`}
                          >
                            <Link
                              to="#"
                              className="page-link"
                              onClick={(e) => {
                                e.preventDefault();
                                setControlledPageIndex ? setControlledPageIndex(pageNum) : setPageIndex(pageNum);
                              }}
                              aria-label={`Página ${pageNum + 1}`}
                              aria-current={currentPage === pageNum ? "page" : undefined}
                            >
                              {pageNum + 1}
                            </Link>
                          </li>
                        ))}

                        {/* Ellipsis final si hay páginas ocultas al final */}
                        {showEndEllipsis && (
                          <li className="paginate_button page-item disabled">
                            <span className="page-link" aria-hidden="true">...</span>
                          </li>
                        )}
                      </>
                    );
                  })()}

                  {/* Botón Next */}
                  <li className={`paginate_button page-item next ${!getCanNextPage() ? "disabled" : ""}`}>
                    <Link to="#" className="page-link" onClick={nextPage} aria-label="Página siguiente">
                      <i className="mdi mdi-chevron-right"></i>
                    </Link>
                  </li>

                  {/* Botón Last */}
                  <li className={`paginate_button page-item ${getState().pagination.pageIndex === getPageOptions().length - 1 ? "disabled" : ""}`}>
                    <Link
                      to="#"
                      className="page-link"
                      onClick={(e) => {
                        e.preventDefault();
                        const lastPage = getPageOptions().length - 1;
                        if (getState().pagination.pageIndex !== lastPage) {
                          setControlledPageIndex ? setControlledPageIndex(lastPage) : setPageIndex(lastPage);
                        }
                      }}
                      aria-label="Última página"
                    >
                      <i className="mdi mdi-chevron-double-right"></i>
                    </Link>
                  </li>
                </ul>
              </div>
            </Col>
          </Row>
        )
      }
    </Fragment>
  );
};

export default TableContainer;